import { yupResolver } from '@hookform/resolvers/yup'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid2'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Stack from '@mui/material/Stack'
import { CustomNextAuthErrors } from '@repo/shared-account-auth-types/next-auth'
import { FormProvider, RHFCheckbox, RHFTextField } from '@repo/shared-core-ui-components/forms'
import { RHFCode } from '@repo/shared-core-ui-components/forms/ReactHookForm/code/rhf-code'
import { Iconify } from '@repo/shared-core-ui-components/Iconify/iconify'
import { signIn } from 'next-auth/react'
import { useRouter, useSearchParams } from 'next/navigation'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import * as Yup from 'yup'

// --------------------------------------------------------------------------------------------- //

interface FormValuesProps {
  email?: string
  password?: string
  token?: string
  remember?: boolean
}

const defaultValues: FormValuesProps = {
  email: '',
  password: '',
  token: '',
  remember: false
}

// --------------------------------------------------------------------------------------------- //

interface Props {
  defaultReturnUrl?: string
}

export function LoginForm({ defaultReturnUrl }: Props) {
  const router = useRouter()
  const searchParams = useSearchParams()

  const [showPassword, setShowPassword] = useState(false)
  const [showTokenField, setShowTokenField] = useState(false)

  const FormSchema = Yup.object().shape({
    email: Yup.string().required('Email is required').email('That is not an email'),
    password: Yup.string().required('Password is required').min(6, 'Password should be of minimum 6 characters length'),
    token: Yup.string().when([], {
      is: () => showTokenField === true,
      then: schema => schema.required('2FA Token is Required').test('len', 'Token must be 6 characters', val => val.length === 6)
    }),
    remember: Yup.boolean()
  })

  const methods = useForm({
    mode: 'onTouched',
    resolver: yupResolver<any>(FormSchema),
    defaultValues
  })

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting, isDirty },
    setError,
    setValue,
    setFocus
  } = methods

  // const values = watch()

  const handleShowPassword = () => setShowPassword(show => !show)

  const onSubmit = async (data: FormValuesProps) => {
    try {
      const loginResponse = await signIn(`credentials`, {
        redirect: false,
        email: data.email,
        password: data.password,
        token: data.token
      })

      // console.log('loginResponse: ', loginResponse)

      if (loginResponse?.error === ('INVALID_CREDENTIALS' as CustomNextAuthErrors)) {
        toast.error(`Invalid Credentials!`)
        setValue('password', '')
        return
      }

      if (loginResponse?.error === ('ACCESS_DENIED' as CustomNextAuthErrors)) {
        toast.error(`Access Denied!`)
        setValue('password', '')
        return
      }

      if (loginResponse?.error === ('2FA_REQUIRED' as CustomNextAuthErrors)) {
        setShowTokenField(true)
        setError('token', { type: 'required', message: '2FA Token is Required' }, { shouldFocus: true })
        setFocus('token')
        return
      }

      if (loginResponse?.error === ('2FA_FAILURE' as CustomNextAuthErrors)) {
        toast.error(`2FA Verification Failed!`)
        setError('token', { type: 'validate', message: '2FA Verification Failed! Try again' }, { shouldFocus: true })
        setValue('token', '')
        return
      }

      if (loginResponse?.ok === true && !loginResponse?.error) {
        const returnUrl = (searchParams.get('returnUrl') as string) ?? defaultReturnUrl ?? '/dashboard'
        router.push(returnUrl)
      } else {
        toast.error(`Invalid Credentials!`)
      }
    } catch (e) {
      console.error('Login User Error Caught: ', e)
      toast.warning('Error Logging in')
    }
    setShowTokenField(false)
    reset()
  }

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 12 }}>
          <Stack spacing={3}>
            <RHFTextField name="email" label="Email address" />
            <RHFTextField
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleShowPassword} edge="end">
                      <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            {showTokenField && <RHFCode name="token" />}

            <RHFCheckbox name="remember" label="Remember Me" />

            <Button id="login-button" fullWidth color="info" size="large" type="submit" variant="contained" loading={isSubmitting} disabled={!isDirty}>
              Submit
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </FormProvider>
  )
}
